import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import { Users, Shield, Settings, Trash2, UserPlus, UserX, UserCheck } from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const [passkey, setPasskey] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!user || typeof user !== "object") {
    return <Navigate to="/login" replace />;
  }

  const handlePasskeySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const res = await api.post("/admin/verify-passkey", { passkey });
      if (res.data.valid) {
        setAuthenticated(true);
        loadData();
      } else {
        setError("Invalid passkey");
      }
    } catch (e) {
      setError("Verification failed");
    }
    setLoading(false);
  };

  const loadData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Delete this user?")) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      loadData();
    } catch (e) {
      alert("Failed to delete user");
    }
  };

  const handleToggleUser = async (userId, enabled) => {
    try {
      const endpoint = enabled ? "disable" : "enable";
      await api.post(`/admin/users/${userId}/${endpoint}`);
      loadData();
    } catch (e) {
      alert("Failed to update user");
    }
  };

  if (!authenticated) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-full max-w-md p-8">
            <div className="text-center mb-8">
              <Shield className="h-16 w-16 mx-auto text-slate-900" />
              <h1 className="mt-4 text-2xl font-bold">Admin Panel</h1>
              <p className="mt-2 text-slate-600">Enter admin passkey to continue</p>
            </div>
            
            <form onSubmit={handlePasskeySubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="passkey">Admin Passkey</Label>
                <Input
                  id="passkey"
                  type="password"
                  value={passkey}
                  onChange={(e) => setPasskey(e.target.value)}
                  placeholder="Enter passkey"
                />
              </div>
              
              {error && (
                <div className="text-red-600 text-sm">{error}</div>
              )}
              
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? "Verifying…" : "Access Admin"}
              </Button>
            </form>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="h-8 w-8" />
          <h1 className="text-2xl font-bold">Admin Panel</h1>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-6 rounded-lg border">
              <Users className="h-8 w-8 text-blue-600 mb-2" />
              <div className="text-3xl font-bold">{stats.users.total}</div>
              <div className="text-sm text-slate-600">Total Users</div>
            </div>
            <div className="bg-white p-6 rounded-lg border">
              <Shield className="h-8 w-8 text-red-600 mb-2" />
              <div className="text-3xl font-bold">{stats.users.admins}</div>
              <div className="text-sm text-slate-600">Admins</div>
            </div>
            <div className="bg-white p-6 rounded-lg border">
              <Users className="h-8 w-8 text-green-600 mb-2" />
              <div className="text-3xl font-bold">{stats.users.normal}</div>
              <div className="text-sm text-slate-600">Normal Users</div>
            </div>
            <div className="bg-white p-6 rounded-lg border">
              <Settings className="h-8 w-8 text-purple-600 mb-2" />
              <div className="text-3xl font-bold">{stats.creations}</div>
              <div className="text-sm text-slate-600">Creations</div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              All Users
            </h2>
            <Button onClick={loadData} variant="outline" size="sm">
              Refresh
            </Button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Name</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Email/Phone</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Method</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Role</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t">
                    <td className="p-4">{u.name}</td>
                    <td className="p-4">{u.email || u.phone || "-"}</td>
                    <td className="p-4">
                      <span className="text-xs bg-slate-100 px-2 py-1 rounded">
                        {u.login_method}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded ${
                        u.role === "admin" ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded ${
                        u.enabled === false ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
                      }`}>
                        {u.enabled === false ? "Disabled" : "Active"}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        {u.enabled !== false ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleUser(u.id, true)}
                          >
                            <UserX className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleUser(u.id, false)}
                          >
                            <UserCheck className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteUser(u.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {users.length === 0 && (
            <div className="p-8 text-center text-slate-500">
              No users found
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}